import {useConfirm} from '@primer/react'
import {useCallback} from 'react'

import {FileExport} from '../api/stats/contracts'
import {useCopyPaste} from '../components/table/hooks/use-copy-paste'
import {ContentType} from '../platform/content-type'
import {useProjectDetails} from '../state-providers/memex/use-project-details'
import {useMemexItems} from '../state-providers/memex-items/use-memex-items'
import {usePostStats} from './common/use-post-stats'
import {useViews} from './use-views'

const escapeFileName = (str: string) => str.replace(/[\\/:"*?<>|]/g, '')

export const useDownloadView = () => {
  const {itemsToCsv} = useCopyPaste()
  const {currentView} = useViews()
  const {items} = useMemexItems()
  const projectName = useProjectDetails().title
  const {postStats} = usePostStats()
  const confirm = useConfirm()

  return useCallback(async () => {
    if (!currentView) return

    const visibleItems = [...items]
    const content = itemsToCsv(visibleItems, {withHeaders: true})
    if (!content) return

    const shouldExport = await confirm({
      title: 'Export view data',
      content: 'Only rows that have been loaded into this view will be exported.',
      confirmButtonContent: 'Export',
      confirmButtonType: 'primary',
    })

    if (shouldExport) {
      postStats({name: FileExport, number_of_rows: visibleItems.length})

      const name = `${escapeFileName(projectName)} - ${escapeFileName(currentView.name)}.tsv`
      const blob = new Blob([content], {
        type: ContentType.CSV,
      })

      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }
  }, [currentView, items, itemsToCsv, projectName, postStats, confirm])
}
